import {
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Spinner,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Contractor } from "@components/modules/contractors/ContractorModel";
import ModalContractor from "@components/modules/contractors/ModalContractor";
import {
  Card,
  FormRow,
  formatDate,
  ModalConfirm,
  Select,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseTypeEnum,
  useCreateContractorMutation,
} from "@src/__generated__/graphql";
import {
  ContractorSelectOptionsDocument,
  type ContractorSelectOptionsQuery,
  type ContractorSelectOptionsQueryVariables,
  ExpensePaymentTypeEnum,
  useContractorSelectOptionsQuery,
} from "@src/__generated__/urql-graphql";
import { ModalContractorForm } from "@src/components/modules/contractors/ModalContractor/ModalContractorForm";
import { LargeSelect } from "@src/components/ui-kit/Select/LargeSelect";
import {
  fieldToCheckboxProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { observer } from "mobx-react-lite";
import { Fragment, useEffect, useState } from "react";
import { useClient } from "urql";
import {
  useFetchContract,
  useFetchContractsOptions,
  useScrollToWhen,
} from "../../hooks";
import { useStore } from "../../store/context";
import { OcrBeta } from "../OcrBeta";
import { ContractorDetails } from "./ContractorDetails";
import { ModalCreateContract } from "./ModalCreateContract";

export const BasicInfoCard = observer(function BasicInfoCard() {
  const store = useStore();
  const { form } = store;
  const createContractModal = useDisclosure();
  const [createContractorStore] = useState(() => new ModalContractor.Store());
  const client = useClient();
  const loadingContractsOptions = useFetchContractsOptions();
  const formRowRef = useScrollToWhen(store.scrollTo.basicInfoCard);
  const [showContractorForm, { toggle: toggleShowContractorForm }] =
    useBoolean(false);
  const predictionFetching =
    store.isPredictionFetching || store.predictionFillingForm;

  const [{ data: contractorsData, fetching: contractorsFetching }] =
    useContractorSelectOptionsQuery({
      pause: !store?.fileContractor?.legal_name,
      variables: {
        page: 1,
        first: 1,
        filters: {
          search: store?.fileContractor?.legal_name,
        },
      },
    });

  const contractors = contractorsData?.contractors?.data;

  useEffect(() => {
    if (!contractors?.length) return;

    const matchingContractorId = contractors[0]?.id;

    if (!matchingContractorId) return;

    onSelectContractor(matchingContractorId);
  }, [contractors]);

  useFetchContract();

  const onSelectType = (val: ExpenseTypeEnum) => {
    form.type.onChange(val);
    form.contract_id.reset();
    store.resetRelatedDocuments();
    if (val === ExpenseTypeEnum.Bill) {
      form.already_paid.onChange(true);
    } else if (val === ExpenseTypeEnum.Card) {
      const paymentTypeId = store.paymentTypeOptions.find(
        ({ system_name }) => system_name === ExpensePaymentTypeEnum.Card,
      )?.value;
      form.already_paid.onChange(true);
      form.payment_type_id.onChange(paymentTypeId);
    } else {
      form.already_paid.reset();
    }
    store.fetchExpenseNo(
      val === ExpenseTypeEnum.Bill || val === ExpenseTypeEnum.Card
        ? form.userCreatedDate.value
        : form.date_of_supply.value,
      form.workspace_company_id.value,
      store.sequenceType,
    );
  };

  const loadContractorOptions = async (
    page: number,
    perPage: number,
    searchPhrase: string,
  ) => {
    return await client
      .query<
        ContractorSelectOptionsQuery,
        ContractorSelectOptionsQueryVariables
      >(ContractorSelectOptionsDocument, {
        page,
        first: perPage,
        filters: {
          search: searchPhrase,
        },
      })
      .toPromise()
      .then((res) => {
        res.data?.contractors?.paginatorInfo;
        return {
          data: (res.data?.contractors?.data || []).map((c) => ({
            label: c.legal_name,
            value: c.id,
          })),
          paginatorInfo: res.data?.contractors?.paginatorInfo,
        };
      });
  };

  const [create, { loading: loadingCreateContractor }] =
    useCreateContractorMutation({
      onCompleted({ createContractor }) {
        form.contractor_id.onChange(createContractor.id);
        store.setContractor(new Contractor(createContractor));
        store.refetchDependencies?.();
        createContractorStore.toggleOpen(false);
      },
    });

  const onSelectContractor = (val: string) => {
    const currentContractor = contractors?.find((c) => c.id === val);
    store.activeContractorOption = currentContractor
      ? {
          label: currentContractor?.legal_name,
          value: currentContractor?.id,
        }
      : undefined;
    form.contractor_id.reset();
    form.contractor_id.onChange(val);
    form.contract_id.reset();
    store.resetRelatedDocuments();
    form.expense_items.forEach((item) => {
      item.form.$.accounting_account_id.onChange("");
    });
    if (val) {
      store.fetchContractor(val);
    }
  };

  const onCreateContractor = async () => {
    const { hasError } =
      await createContractorStore.form.validateForContractor();
    if (hasError) return;
    await create({
      variables: { input: createContractorStore.form.serialize() },
    });

    if (!showContractorForm) return;
    toggleShowContractorForm();
  };

  const onOpenModal = () => {
    createContractorStore.form.reset();
    createContractorStore.toggleOpen(true);
  };

  const handleCreateContractorOpen = () => {
    createContractorStore.form.reset();
    toggleShowContractorForm();
    const contractorForm = createContractorStore.form;
    const {
      legal_name,
      tax_no,
      vat_no,
      street,
      city,
      zip,
      bankAccounts,
      country,
    } = store.fileContractor;

    createContractorStore.setCompanyData(
      {
        vat_no,
        tax_no,
        street,
        city,
        zip,
        legal_name,
        country,
        cancelled: false,
        applies_vat: false,
        bankAccounts: bankAccounts ?? [],
      },
      {
        bankAccounts: bankAccounts ?? [],
      },
    );

    if (createContractorStore.form.extraValidationEnabled) {
      createContractorStore.form.vat_payer.onChange(true);
    }

    if (!country) {
      const countryOptionsLength = contractorForm.countryOptions.length;

      for (let i = 0; i < countryOptionsLength; i++) {
        const { label, value } = contractorForm.countryOptions[i];

        if (
          !contractorForm.street.value
            .toLowerCase()
            .includes(label.toLowerCase())
        )
          continue;
        const newVal = contractorForm.street.value.replace(label, "");
        contractorForm.street.onChange(newVal);
        contractorForm.city.onChange(newVal);
        contractorForm.zip.onChange(newVal);

        if (contractorForm.country_id.value === value) break;
        contractorForm.country_id.onChange(value);
        break;
      }
    }
  };

  return (
    <Card sectioned pos="relative">
      {predictionFetching ? (
        <VStack align="center" justify="center" h="136px" spacing="4">
          <Spinner color="teal.500" size="xl" thickness="3px" />
          <Text fontSize="large">
            <Trans>Extracting data, please wait...</Trans>
          </Text>
        </VStack>
      ) : (
        <Fragment>
          <FormRow title={t`Choose type`} ref={formRowRef}>
            <Select
              isDisabled={!store.canUserAccess("type")}
              placeholder={t`Select type of expense`}
              options={store.typesOptions}
              value={form.type.value}
              onChange={onSelectType}
              error={form.type.error}
            />
          </FormRow>
          <Fragment>
            <FormRow title={t`Choose contractor`}>
              <LargeSelect
                placeholder={t`Select contractor`}
                loadOptions={loadContractorOptions}
                defaultValue={store.contractor?.legal_name}
                defaultInputValue={store.contractor?.legal_name}
                defaultOptions={
                  store.activeContractorOption
                    ? [store.activeContractorOption]
                    : undefined
                }
                topExtraContent={
                  <Button
                    className="p-6"
                    p="4"
                    onClick={onOpenModal}
                    variant="link"
                  >
                    <Trans>+ Add new contractor</Trans>
                  </Button>
                }
                options={[]}
                value={form.contractor_id.value}
                onChange={onSelectContractor}
                error={form.contractor_id.error}
              />
            </FormRow>
            {Array.isArray(contractors) && !contractors.length && (
              <FormRow>
                <VStack align="start">
                  <Text color="red.500">
                    <OcrBeta />
                    &nbsp;<Trans>didn't find matching contractor</Trans>
                  </Text>
                  <Button
                    onClick={handleCreateContractorOpen}
                    size="sm"
                    variant="subtle"
                  >
                    <Trans>+ Add new contractor</Trans>
                  </Button>
                </VStack>
              </FormRow>
            )}
            <FormRow>
              <ContractorDetails
                loading={store.isFetchingContractor || contractorsFetching}
              />
            </FormRow>
            {store.expense?.emailData && (
              <FormRow>
                <Button onClick={store.emailModalState.onOpen} size="sm">
                  <Trans>Show email</Trans>
                </Button>
                <ModalConfirm
                  isOpen={store.emailModalState.isOpen}
                  onClose={store.emailModalState.onClose}
                  heading={t`Email Information`}
                  hideCancelButton
                  onConfirm={store.emailModalState.onClose}
                  size="2xl"
                  confirmBtnTitle={t`Close`}
                >
                  <Stack spacing="4">
                    <Stack spacing="2">
                      <Text fontWeight="bold">
                        <Trans>From</Trans>
                      </Text>
                      <Text>{store.expense.emailData.from}</Text>
                    </Stack>

                    <Stack spacing="2">
                      <Text fontWeight="bold">
                        <Trans>Subject</Trans>
                      </Text>
                      <Text>{store.expense.emailData.subject}</Text>
                    </Stack>

                    <Stack spacing="2">
                      <Text fontWeight="bold">
                        <Trans>Received At</Trans>
                      </Text>
                      <Text>
                        {formatDate(
                          new Date(store.expense.emailData.received_at),
                        )}
                      </Text>
                    </Stack>

                    <Stack spacing="2">
                      <Text fontWeight="bold">
                        <Trans>Message</Trans>
                      </Text>
                      <Text whiteSpace="pre-wrap">
                        {store.expense.emailData.text}
                      </Text>
                    </Stack>
                  </Stack>
                </ModalConfirm>
              </FormRow>
            )}
            {store.canUserAccess("contract_id") &&
              !store.isFetchingContractor && (
                <FormRow>
                  <Select
                    isSearchable
                    placeholder={t`Select contract`}
                    isLoading={loadingContractsOptions}
                    {...fieldToSelectProps(
                      store.form.contract_id,
                      store.contractsOptions,
                    )}
                    topExtraContent={
                      <Button
                        p="4"
                        onClick={createContractModal.onOpen}
                        variant="link"
                      >
                        <Trans>+ Add new contract</Trans>
                      </Button>
                    }
                  />
                </FormRow>
              )}
          </Fragment>
          {store.canUserAccess("two_percent_fund") && (
            <FormRow
              title={
                <FormControl alignItems="center" display="flex">
                  <FormLabel mb="0">{t`2% found payment`}</FormLabel>
                  <Checkbox
                    isDisabled={!!store.expense?.id}
                    {...fieldToCheckboxProps(store.form.two_percent_fund)}
                  />
                </FormControl>
              }
            >
              {store.form.two_percent_fund.$ && (
                <Select
                  placeholder={t`Select`}
                  isDisabled={!!store.expense?.id}
                  {...fieldToSelectProps(
                    store.form.fond_contractor_id,
                    store.fondContractorsOptions,
                  )}
                />
              )}
            </FormRow>
          )}
          <Collapse in={showContractorForm}>
            <ModalContractorForm store={createContractorStore} />
            <HStack justify="flex-end" spacing="2">
              <Button onClick={toggleShowContractorForm} variant="outline">
                <Trans>Cancel</Trans>
              </Button>
              <Button
                isLoading={loadingCreateContractor}
                onClick={onCreateContractor}
              >
                <Trans>Create</Trans>
              </Button>
            </HStack>
          </Collapse>
          <ModalCreateContract {...createContractModal} />
          <ModalContractor.StoreContext.Provider value={createContractorStore}>
            <ModalContractor.Modal
              title={t`Create contractor`}
              primaryAction={{
                onAction: onCreateContractor,
                loading: loadingCreateContractor,
              }}
            />
          </ModalContractor.StoreContext.Provider>
        </Fragment>
      )}
    </Card>
  );
});
